"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.cvBankUploadSchema = exports.resolveCvBankDuplicateSchema = exports.CV_BANK_DUPLICATE_CHOICES = exports.restoreCvBankSchema = exports.removeMatchingFromCvBankSchema = exports.CV_BANK_MATCHING_MAX = exports.removeFromCvBankSchema = exports.addToCvBankSchema = exports.cvBankListQuerySchema = exports.CV_BANK_NO_CV = exports.CV_BANK_BATCH_MAX = exports.CV_BANK_EVENTS = exports.CV_BANK_ENTITY = exports.CV_BANK_KEEP_REASON = exports.CV_BANK_SOURCE_LABELS = exports.CV_BANK_SOURCES = void 0;
exports.suggestContactFromCvText = suggestContactFromCvText;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// ADR 0046 — the CV Bank: a person plus one of their CV files, added on purpose and kept until
// someone removes it (DECISIONS-LOG 2026-10-14 Q1–Q4). Responses keep the person under a
// `candidate` key, so the serialiser masks their contact details like every list (#5).
/** The four ways in (Q2): the profile, ticked rows, an upload straight into the bank, the reject box. */
exports.CV_BANK_SOURCES = ['profile', 'bulk', 'upload', 'reject'];
exports.CV_BANK_SOURCE_LABELS = {
    profile: 'From their profile',
    bulk: 'From a list',
    upload: 'Uploaded',
    reject: 'On rejecting',
};
/** Q1: the retention keep the bank sets, and the only one it ever releases. */
exports.CV_BANK_KEEP_REASON = 'In the CV Bank';
exports.CV_BANK_ENTITY = 'cv_bank_entry';
exports.CV_BANK_EVENTS = {
    ADDED: 'cv_bank.added',
    REMOVED: 'cv_bank.removed',
    DUPLICATE_RESOLVED: 'cv_bank.duplicate_resolved',
};
/** One request adds or removes at most this many — the bulk limit everywhere else (§L9). */
exports.CV_BANK_BATCH_MAX = 200;
exports.CV_BANK_NO_CV = 'has no CV to keep. Upload one on their profile first.';
// --- Requests ------------------------------------------------------------------------------
exports.cvBankListQuerySchema = schemas_1.listQuerySchema.extend({
    source: zod_1.z.enum(exports.CV_BANK_SOURCES).optional(),
});
/** From the profile or ticked rows; an upload and the reject box have their own paths. */
exports.addToCvBankSchema = zod_1.z.object({
    candidateIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one person.').max(exports.CV_BANK_BATCH_MAX, `Add up to ${exports.CV_BANK_BATCH_MAX} at a time.`),
    source: zod_1.z.enum(['profile', 'bulk']),
});
const removalReasonSchema = zod_1.z.string().trim().min(3, 'Say why, in a few words.').max(300, 'Keep the reason under 300 characters.');
exports.removeFromCvBankSchema = zod_1.z.object({
    entryIds: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Choose at least one CV.').max(exports.CV_BANK_BATCH_MAX),
    reason: removalReasonSchema,
});
/** The most entries "Select all N matching" removes at once — the candidate list's bulk limit. */
exports.CV_BANK_MATCHING_MAX = 1000;
/**
 * "Select all N matching" then Remove (Redesign Group 8 Q2 a): the server removes every live entry
 * the list's search and source filter match, less the rows unticked, in one go with one reason.
 */
exports.removeMatchingFromCvBankSchema = zod_1.z.object({
    matching: zod_1.z.object({
        query: zod_1.z.string().trim().min(2).max(200).optional(),
        source: zod_1.z.enum(exports.CV_BANK_SOURCES).optional(),
    }),
    excludedEntryIds: zod_1.z.array(schemas_1.uuidSchema).max(exports.CV_BANK_MATCHING_MAX).default([]),
    reason: removalReasonSchema,
});
/** Undo of a removal (§5a rule 8): the same entries come back as they were — all of a matching remove at once. */
exports.restoreCvBankSchema = zod_1.z.object({
    entryIds: zod_1.z.array(schemas_1.uuidSchema).min(1).max(exports.CV_BANK_MATCHING_MAX),
});
exports.CV_BANK_DUPLICATE_CHOICES = ['first', 'second', 'both'];
/** Q4: keep the first, keep the second, or keep both. */
exports.resolveCvBankDuplicateSchema = zod_1.z
    .object({
    firstEntryId: schemas_1.uuidSchema,
    secondEntryId: schemas_1.uuidSchema,
    keep: zod_1.z.enum(exports.CV_BANK_DUPLICATE_CHOICES),
})
    .refine((input) => input.firstEntryId !== input.secondEntryId, { path: ['secondEntryId'], message: 'Choose two different CVs.' });
/**
 * The fields beside one uploaded CV (ADR 0046 §2): a name and an email or a phone, checked by a
 * person, and consent — the same rule as adding anyone by hand (F7.10, D15).
 */
exports.cvBankUploadSchema = zod_1.z
    .object({
    fullName: zod_1.z.string().trim().min(2, 'Enter the full name.').max(160),
    email: schemas_1.emailSchema.max(254).nullable().default(null),
    phone: schemas_1.phoneSchema.nullable().default(null),
    hasConsent: zod_1.z.literal(true, 'Confirm they agreed to us keeping their CV.'),
})
    .refine((value) => value.email !== null || value.phone !== null, {
    path: ['phone'],
    message: 'Give an email or a mobile number, so we can recognise this person next time.',
});
// --- Reading a name, email and phone off a CV ----------------------------------------------
const EMAIL_IN_TEXT = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
/** A Bangladeshi mobile however it is written: +880 1712-345678, 01712 345 678. */
const PHONE_IN_TEXT = /(?:\+?88)?0?1[3-9](?:[\s.-]?\d){8}/;
const BD_MOBILE_CORE = /^1[3-9]\d{8}$/;
const HEADING_WORDS = /^(curriculum\s+vit(a|ae)|resume|résumé|cv|bio[\s-]?data|profile|personal\s+details)$/i;
const FILE_NAME_NOISE = /\b(cv|resume|résumé|curriculum|vitae|final|updated|new)\b/gi;
const NAME_LINES_LOOKED_AT = 10;
const NAME_MIN = 2;
const NAME_MAX = 80;
const MAX_DIGITS_IN_A_NAME = 2;
/**
 * ADR 0046 §2: what the upload fills in for a person to check — never what is saved without them.
 * Email and phone by pattern; the name is the first line that reads like one, else the file name.
 */
function suggestContactFromCvText(text, fileName) {
    return {
        fullName: nameFromLines(text) ?? nameFromFileName(fileName),
        email: EMAIL_IN_TEXT.exec(text)?.[0].toLowerCase() ?? null,
        phone: phoneFrom(text),
    };
}
function phoneFrom(text) {
    const found = PHONE_IN_TEXT.exec(text)?.[0];
    if (!found)
        return null;
    const core = found.replace(/\D/g, '').replace(/^880/, '').replace(/^88/, '').replace(/^0/, '');
    return BD_MOBILE_CORE.test(core) ? `0${core}` : null;
}
function nameFromLines(text) {
    const lines = text
        .split(/\r?\n/)
        .map((line) => line.replace(/\s+/g, ' ').trim().replace(/:$/, ''))
        .filter((line) => line !== '')
        .slice(0, NAME_LINES_LOOKED_AT);
    return lines.find(isNameLike) ?? null;
}
function isNameLike(line) {
    if (line.length < NAME_MIN || line.length > NAME_MAX)
        return false;
    if (HEADING_WORDS.test(line) || line.includes('@') || line.includes('://'))
        return false;
    if ((line.match(/\d/g) ?? []).length > MAX_DIGITS_IN_A_NAME)
        return false;
    return /\p{L}/u.test(line);
}
function nameFromFileName(fileName) {
    const base = fileName.replace(/\.[^.]+$/, '');
    return base.replace(/[_.\-]+/g, ' ').replace(FILE_NAME_NOISE, ' ').replace(/\s+/g, ' ').trim();
}
//# sourceMappingURL=cv-bank.js.map